import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Product } from '@catalyst-psa/shared';
import { PSA_CLASS_LABELS } from '@catalyst-psa/shared';

import { FIELD_CONTROL_CLASS } from '../../components/ui/Field.js';
import { filterProducts, formatProductLabel } from './format.js';

/** Cap on rendered matches so a large catalog never stalls the dropdown. */
const MAX_VISIBLE = 100;

interface ProductPickerProps {
  products: Product[];
  /**
   * Narrowed list shown before the user types anything (e.g. products matching the
   * opportunity type). Typing always searches the full `products` catalog.
   */
  suggestedProducts?: Product[];
  /** Selected product id, or '' when nothing is picked. */
  value: string;
  onChange: (productId: string) => void;
  /** Omit to render without a label (e.g. inside a table cell). */
  label?: string;
  /** Label for the "clear selection" row; omit to make a selection mandatory. */
  emptyLabel?: string;
  required?: boolean;
  error?: string | undefined;
  disabled?: boolean;
  placeholder?: string;
  /** Product ids to exclude from the list (e.g. already attached). */
  excludeIds?: string[];
  /** Extra class names for the input (used to match inline table styling). */
  inputClassName?: string;
  ariaLabel?: string;
}

/**
 * Single-select product combobox with one search box that matches SKU, name, PSA class,
 * customer description and service category at once.
 */
export function ProductPicker({
  products,
  suggestedProducts,
  value,
  onChange,
  label,
  emptyLabel,
  required,
  error,
  disabled,
  placeholder = 'Search SKU, name, class, description, category…',
  excludeIds,
  inputClassName,
  ariaLabel,
}: ProductPickerProps): JSX.Element {
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = products.find((product) => product.id === value) ?? null;

  const matches = useMemo(() => {
    const excluded = new Set(excludeIds ?? []);
    const source = query.trim() ? products : (suggestedProducts ?? products);
    const available = source.filter((product) => !excluded.has(product.id) || product.id === value);
    return filterProducts(available, query);
  }, [products, suggestedProducts, excludeIds, value, query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const commit = (productId: string): void => {
    onChange(productId);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={id} className="text-sm font-medium text-slate-700">
          {label}
          {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-label={label ? undefined : ariaLabel}
          autoComplete="off"
          disabled={disabled}
          className={`w-full ${inputClassName ?? FIELD_CONTROL_CLASS}`}
          placeholder={placeholder}
          value={open ? query : selected ? formatProductLabel(selected) : ''}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              e.stopPropagation();
            }
          }}
        />
        {open ? (
          <ul
            id={`${id}-list`}
            role="listbox"
            className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          >
            {emptyLabel ? (
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit('')}
                  className="w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
                >
                  {emptyLabel}
                </button>
              </li>
            ) : null}
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-500">No matching products</li>
            ) : null}
            {matches.slice(0, MAX_VISIBLE).map((product) => {
              const meta = [
                product.name,
                product.psaClass ? PSA_CLASS_LABELS[product.psaClass] : null,
                product.serviceCategoryName,
              ].filter((part): part is string => Boolean(part));
              return (
                <li key={product.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(product.id)}
                    className={`w-full px-3 py-2 text-left hover:bg-slate-50 ${
                      product.id === value ? 'bg-brand-50' : ''
                    }`}
                  >
                    <span className="block truncate text-sm text-slate-900">
                      {formatProductLabel(product)}
                    </span>
                    {meta.length > 0 ? (
                      <span className="block truncate text-xs text-slate-500">
                        {meta.join(' · ')}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
            {matches.length > MAX_VISIBLE ? (
              <li className="px-3 py-2 text-xs text-slate-500">
                Showing first {MAX_VISIBLE} of {matches.length} matches — keep typing to narrow.
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}
